import React, { useState, useEffect } from "react";
import { HeartPulse, Bell, Brain, Wallet, Users, ShieldCheck, ListChecks, RefreshCw, Loader2 } from "lucide-react";
import { nexusListMemory, nexusGetProfile, nexusFeed, nexusGetLedger, nexusRunEngine, nexusGetPortfolio } from "../lib/api";
import NexusBackdrop from "./NexusBackdrop";
import { toast } from "sonner";

function Panel({ title, icon: Icon, children, testid, accent = "#00F0FF" }) {
  return (
    <div className="nx-glass rounded-2xl p-4 flex flex-col min-h-0" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3 shrink-0">
        <Icon className="w-4 h-4" style={{ color: accent }} />
        <span className="nx-orbitron text-xs tracking-[0.25em] font-bold" style={{ color: accent }}>{title}</span>
      </div>
      <div className="overflow-y-auto min-h-0 flex-1 space-y-2">{children}</div>
    </div>
  );
}

export default function NexusLife({ session }) {
  const [memory, setMemory] = useState([]);
  const [profile, setProfile] = useState(null);
  const [feed, setFeed] = useState([]);
  const [ledger, setLedger] = useState(null);
  const [prep, setPrep] = useState([]);
  const [portfolio, setPortfolio] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadLedger = async () => {
    try { const r = await nexusGetLedger(session.user_id); setLedger(r.ledger || {}); setPrep(r.prep || []); } catch (e) {}
  };
  useEffect(() => {
    (async () => {
      try { setMemory((await nexusListMemory(session.user_id)) || []); } catch (e) {}
      try { setProfile(await nexusGetProfile(session.user_id)); } catch (e) {}
      try { setFeed((await nexusFeed(session.user_id)) || []); } catch (e) {}
      try { setPortfolio(await nexusGetPortfolio(session.user_id)); } catch (e) {}
      loadLedger();
    })();
    const t = setInterval(async () => { try { setPortfolio(await nexusGetPortfolio(session.user_id)); } catch (e) {} }, 30000);
    return () => clearInterval(t);
  }, [session.user_id]); // eslint-disable-line

  const runEngine = async () => {
    setBusy(true); toast.info("Nexus is reviewing your day…");
    try { await nexusRunEngine(session.user_id); await loadLedger(); toast.success("Welfare review updated"); }
    catch (e) { toast.error("Could not run review"); } finally { setBusy(false); }
  };

  const family = Array.isArray(profile?.family)
    ? profile.family.map((f) => (typeof f === "string" ? f : `${f.name || ""}${f.relation ? " (" + f.relation + ")" : ""}`.trim())).filter(Boolean)
    : (profile?.family || "").split(/[,\n]/).map((f) => f.trim()).filter(Boolean);

  return (
    <div className="relative min-h-[calc(100vh-52px)]" data-testid="nexus-life">
      <NexusBackdrop colorHex="#10B981" />
      <div className="relative z-10 p-4 grid grid-cols-1 md:grid-cols-3 auto-rows-[minmax(200px,auto)] gap-4">
      {/* Welfare Ledger — Nexus's standing assessment */}
      <Panel title="WELFARE LEDGER" icon={ShieldCheck} testid="life-ledger" accent="#10B981">
        {!ledger || Object.keys(ledger).length === 0 ? (
          <p className="text-cyan-100/40 text-xs leading-relaxed">Nexus builds this every night — a standing read on your money, health, family, goals and risks. Tap “Review now” to generate it.</p>
        ) : (
          <div className="space-y-2 text-xs">
            {[["Finance", ledger.finance, "#10B981"], ["Health", ledger.health, "#F43F5E"], ["Family", ledger.family, "#38BDF8"], ["Goals", ledger.goals, "#A855F7"], ["Risks", ledger.risks, "#F59E0B"]]
              .filter(([, v]) => v).map(([k, v, c]) => (
                <div key={k} data-testid={`life-ledger-${k.toLowerCase()}`}>
                  <span className="nx-mono text-[10px] tracking-widest" style={{ color: c }}>{k.toUpperCase()}</span>
                  <p className="text-cyan-100/85 leading-snug">{v}</p>
                </div>
              ))}
            {ledger.updated_at && <p className="text-cyan-100/30 text-[10px] pt-1">Updated {new Date(ledger.updated_at).toLocaleString()}</p>}
          </div>
        )}
        <button onClick={runEngine} disabled={busy} data-testid="life-run-engine"
          className="mt-2 flex items-center justify-center gap-2 w-full py-2 rounded-lg text-xs nx-mono border border-[#10B98155] text-[#10B981] hover:bg-[#10B98111] disabled:opacity-40">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Review now
        </button>
      </Panel>

      {/* Tomorrow's prep */}
      <Panel title="PREPARED FOR YOU" icon={ListChecks} testid="life-prep" accent="#00F0FF">
        {prep.length === 0 ? (
          <p className="text-cyan-100/40 text-xs">Each night Nexus lines up what to finish before you start tomorrow.</p>
        ) : prep.map((p, i) => (
          <div key={i} className="flex items-start gap-2 text-xs text-cyan-100" data-testid="life-prep-item">
            <span className="text-[#00F0FF] mt-0.5">▸</span><span className="flex-1">{p}</span>
          </div>
        ))}
      </Panel>

      {/* Health */}
      <Panel title="HEALTH" icon={HeartPulse} testid="life-health" accent="#F43F5E">
        <div className="grid grid-cols-3 gap-2 text-center">
          {[["Recovery", "—"], ["Sleep", "—"], ["Strain", "—"]].map(([k, v]) => (
            <div key={k} className="rounded-xl border border-[#F43F5E33] py-3">
              <p className="text-2xl font-black text-cyan-50">{v}</p>
              <p className="text-[10px] text-cyan-100/50">{k}</p>
            </div>
          ))}
        </div>
        <p className="text-cyan-100/40 text-xs mt-2 leading-relaxed">Connect WHOOP or your iPhone Health via an Apple Shortcut to fill these live. Ready when you are.</p>
      </Panel>

      {/* Reminders / memory tasks */}
      <Panel title="REMINDERS" icon={Bell} testid="life-reminders" accent="#F59E0B">
        {memory.filter((m) => m.when).length === 0 && <p className="text-cyan-100/40 text-xs">No reminders yet — tell Nexus to remind you of anything.</p>}
        {memory.filter((m) => m.when).map((m) => (
          <div key={m.id} className="text-xs text-cyan-100">• {m.text} <span className="text-cyan-300/50">{m.when}</span></div>
        ))}
      </Panel>

      {/* Portfolio / Finances */}
      <Panel title="PORTFOLIO" icon={Wallet} testid="life-finances" accent="#10B981" >
        {!portfolio || !portfolio.count ? (
          <p className="text-cyan-100/40 text-xs">Import your SP Wealth Tracker holdings to see live value & P&L here.</p>
        ) : (
          <>
            <div className="flex items-end justify-between mb-2">
              <div>
                <p className="text-2xl font-black text-cyan-50" data-testid="life-portfolio-value">${portfolio.total_value.toLocaleString()}</p>
                <p className="text-[10px] text-cyan-100/50">{portfolio.count} positions · invested ${portfolio.total_cost.toLocaleString()}</p>
              </div>
              <span className={`text-sm font-bold ${portfolio.total_pl >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid="life-portfolio-pl">
                {portfolio.total_pl >= 0 ? "+" : ""}${portfolio.total_pl.toLocaleString()} ({portfolio.total_pl_pct}%)
              </span>
            </div>
            {portfolio.holdings.slice(0, 12).map((h) => (
              <div key={h.symbol} className="flex items-center justify-between text-xs" data-testid={`life-holding-${h.symbol}`}>
                <span className="font-bold text-cyan-50">{h.symbol}</span>
                <div className="flex items-center gap-2">
                  <span className="text-cyan-100/70">${h.value.toLocaleString()}</span>
                  <span className={`${h.pl >= 0 ? "text-emerald-400" : "text-red-400"} w-14 text-right`}>{h.pl >= 0 ? "+" : ""}{h.pl_pct}%</span>
                </div>
              </div>
            ))}
            <p className="text-cyan-100/30 text-[10px] mt-1">Live P&L via Finnhub · cost basis from SP Wealth Tracker</p>
          </>
        )}
      </Panel>

      {/* Memory graph */}
      <Panel title="WHAT NEXUS KNOWS" icon={Brain} testid="life-memory" accent="#A855F7">
        {memory.length === 0 && <p className="text-cyan-100/40 text-xs">Nexus is still getting to know you.</p>}
        <div className="flex flex-wrap gap-1.5">
          {memory.slice(0, 40).map((m) => (
            <span key={m.id} className="px-2 py-1 rounded-full text-[11px] bg-[#A855F71a] border border-[#A855F740] text-cyan-50" data-testid="life-memory-chip">{m.text}</span>
          ))}
        </div>
      </Panel>

      {/* Family */}
      <Panel title="FAMILY" icon={Users} testid="life-family" accent="#38BDF8">
        {family.length === 0 && <p className="text-cyan-100/40 text-xs">Add your family in Personalize so Nexus can look after them.</p>}
        {family.map((f, i) => (
          <div key={i} className="rounded-xl border border-[#38BDF833] px-3 py-2 text-sm text-cyan-50">{f}</div>
        ))}
      </Panel>

      {/* Profile */}
      <Panel title="YOU" icon={Users} testid="life-you" accent="#00F0FF">
        <p className="text-cyan-50 text-lg font-bold">{profile?.name || "Sohel Patel"}</p>
        {profile?.about && <p className="text-cyan-100/70 text-xs mt-1 leading-relaxed">{profile.about}</p>}
        <p className="text-cyan-100/40 text-[11px] mt-2">{memory.length} facts remembered</p>
      </Panel>
      </div>
    </div>
  );
}
